import React from "react";
import { Space } from "antd";
import {
  AxComment,
  AxThread,
  AxAvatar,
  AxDropdown,
  AxMenu,
  AxDivider,
  AxButton,
} from "@dynata/axiom3";

class Comment extends React.Component {
  render() {
    return (
      <div className="section" id="comment">
        <h3>Comment and Thread</h3>

        <p>Renders a list of Comments inside a Thread component.</p>

        <AxDivider orientation="left">Basic Usage</AxDivider>

        <Space size="large" direction="vertical">
          <div className="prop-header">
            <p className="props-title">Props: AxThread</p>
          </div>
          <table className="props-table">
            <tbody>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Value</th>
              </tr>
              <tr>
                <td>collapseAt</td>
                <td>
                  When the comments count in the thread reaches the value, the
                  list is truncated to show only the first and the last comment,
                  with a button option to expand the list. All comments will
                  show if collapseAt &lt;= 2 or undefined, or if
                  selected == true.
                </td>
                <td>number</td>
              </tr>
              <tr>
                <td>floating</td>
                <td>Set the floating state.</td>
                <td>boolean</td>
              </tr>
              <tr>
                <td>footer</td>
                <td>Footer content</td>
                <td>ReactNode</td>
              </tr>

              <tr>
                <td>header</td>
                <td>Header content</td>
                <td>ReactNode</td>
              </tr>
              <tr>
                <td>inputProps</td>
                <td>Props for input element in the thread</td>
                <td>object</td>
              </tr>
              <tr>
                <td>onExpand</td>
                <td>Called when the 'View more replies' element is clicked.</td>
                <td>(void) =&gt; void</td>
              </tr>
              <tr>
                <td>onPostClick</td>
                <td>
                  Called when the Post button is clicked. The `value` parameter
                  contains the post content.
                </td>
                <td>(value: string) =&gt; void</td>
              </tr>
              <tr>
                <td>selected</td>
                <td>Set the selected state.</td>
                <td>boolean</td>
              </tr>
              <tr>
                <td>resolved</td>
                <td>Hides textarea and post button, if true.</td>
                <td>boolean</td>
              </tr>

              <tr>
                <td>postFooter</td>
                <td>
                  Post footer to render custom controls beside the post button
                </td>
                <td>reactNote</td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to a div element that is the
                  outermost parent of the component.
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div className="prop-header">
            <p className="props-title">Props: AxComment</p>
          </div>
          <table className="props-table">
            <tbody>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Value</th>
              </tr>
              <tr>
                <td>actionMenu</td>
                <td>Content for action menu.</td>
                <td>ReactNode</td>
              </tr>
              <tr>
                <td>avatar</td>
                <td>Provide AxAvatar element.</td>
                <td>ReactNode</td>
              </tr>
              <tr>
                <td>content</td>
                <td>Post's content</td>
                <td>ReactNode</td>
              </tr>
              <tr>
                <td>read</td>
                <td>Read/unread state of the comment</td>
                <td>boolean</td>
              </tr>
              <tr>
                <td>tag</td>
                <td>If provided, shows up beside username.</td>
                <td>string</td>
              </tr>
              <tr>
                <td>timestamp</td>
                <td></td>
                <td>string</td>
              </tr>
              <tr>
                <td>username</td>
                <td></td>
                <td>string</td>
              </tr>
              <tr>
                <td>edited</td>
                <td>States if the comment was edited.</td>
                <td>boolean</td>
              </tr>
              <tr>
                <td>editing</td>
                <td>Toggle edit mode</td>
                <td>boolean</td>
              </tr>
              <tr>
                <td>editId</td>
                <td>
                  The id is returned back with the onEdit and onEditCancel
                  callbacks.
                </td>
                <td>any</td>
              </tr>
              <tr>
                <td>editingText</td>
                <td>The comment text to show in the textarea in edit mode.</td>
                <td>string</td>
              </tr>
              <tr>
                <td>editingFooter</td>
                <td>Placeholder to render extra controls in edit mode.</td>
                <td>ReactNode</td>
              </tr>
              <tr>
                <td>editingInputProps</td>
                <td>
                  Extra props to be passed down to the AxTextArea component in
                  edit mode.
                </td>
                <td>object</td>
              </tr>
              <tr>
                <td>onEdit</td>
                <td></td>
                <td>(text: string, editId: string) =&gt; void</td>
              </tr>
              <tr>
                <td>onEditCancel</td>
                <td></td>
                <td>(editId: string) =&gt; void</td>
              </tr>
            </tbody>
          </table>
          <AxDivider orientation="left">Default</AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxThread
  selected={true}
  inputProps={{ placeholder: "Reply or add others with @" }}
  postFooter={
    <div>
      <AxButton color="neutral" type="link" icononly>
        <i className="fa-regular fa-face-smile"></i>
      </AxButton>

      <AxButton color="neutral" type="link" icononly>
        @
      </AxButton>
    </div>
  }
>
  <AxComment
    actionMenu={
      <AxDropdown
        overlay={<AxMenu size="small"><AxMenu.Item key="0">List Item</AxMenu.Item></AxMenu>}
        trigger={["click"]}
        placement="topLeft"
      >
        <AxButton icononly color="neutral" size="small" type="link">
          <i className="fa-regular fa-ellipsis-vertical"></i>
        </AxButton>
      </AxDropdown>
    }
    avatar={<AxAvatar size="32" src="https://joeschmoe.io/api/v1/random"/>}
    read={true}
    username="Username"
    content={<span>This is a sample comment with an in-line <a>@Mention</a> tag.</span>}
    timestamp="13:50 AM, Mar 20"
  />
</AxThread>
            `}</pre>
            <Space size="large" align="start">
              <div style={{ width: "300px" }}>
                <AxThread
                  selected={false}
                  inputProps={{ placeholder: "Reply or add others with @" }}
                  postFooter={
                    <div>
                      <AxButton color="neutral" type="link" icononly>
                        <i className="fa-regular fa-face-smile"></i>
                      </AxButton>

                      <AxButton color="neutral" type="link" icononly>
                        @
                      </AxButton>
                    </div>
                  }
                >
                  <AxComment
                    avatar={
                      <AxAvatar
                        size="32"
                        src="https://joeschmoe.io/api/v1/random"
                      />
                    }
                    read={true}
                    username="Username"
                    content="This comment is editable."
                    timestamp="13:50 AM, Mar 20"
                    edited={false}
                    editing={true}
                    editingInputProps={{
                      placeholder: "Edit Comment",
                    }}
                    editingText="This comment is editable."
                    editingFooter={
                      <div>
                        <AxButton color="neutral" type="link" icononly>
                          <i className="fa-regular fa-face-smile"></i>
                        </AxButton>

                        <AxButton color="neutral" type="link" icononly>
                          @
                        </AxButton>
                      </div>
                    }
                  />
                  <AxComment
                    actionMenu={
                      <AxDropdown
                        overlay={
                          <AxMenu size="small">
                            <AxMenu.Item key="0">List Item</AxMenu.Item>
                          </AxMenu>
                        }
                        trigger={["click"]}
                        placement="topLeft"
                      >
                        <AxButton
                          icononly
                          color="neutral"
                          size="small"
                          type="link"
                        >
                          <i className="fa-regular fa-ellipsis-vertical"></i>
                        </AxButton>
                      </AxDropdown>
                    }
                    avatar={
                      <AxAvatar
                        size="32"
                        src="https://joeschmoe.io/api/v1/random"
                      />
                    }
                    read={false}
                    username="Username"
                    content={
                      <span>
                        This is a sample comment with an in-line{" "}
                        <a href={"/#"}>@Mention</a> tag.
                      </span>
                    }
                    timestamp="13:50 AM, Mar 20"
                  />

                  <AxComment
                    actionMenu={
                      <AxDropdown
                        overlay={
                          <AxMenu size="small">
                            <AxMenu.Item key="0">List Item</AxMenu.Item>
                          </AxMenu>
                        }
                        trigger={["click"]}
                        placement="topLeft"
                      >
                        <AxButton
                          icononly
                          color="neutral"
                          size="small"
                          type="link"
                        >
                          <i className="fa-regular fa-ellipsis-vertical"></i>
                        </AxButton>
                      </AxDropdown>
                    }
                    avatar={
                      <AxAvatar
                        size="32"
                        src="https://joeschmoe.io/api/v1/random"
                      />
                    }
                    edited={true}
                    read={false}
                    username="Mike"
                    content={<span>Edited comment.</span>}
                    timestamp="13:50 AM, Mar 20"
                  />
                </AxThread>
              </div>

              <div>
                <AxDropdown
                  trigger={["contextMenu"]}
                  placement="topLeft"
                  overlay={
                    <div style={{ width: "300px" }}>
                      <AxThread
                        floating={true}
                        postFooter={
                          <div>
                            <AxButton color="neutral" type="link" icononly>
                              <i className="fa-regular fa-face-smile"></i>
                            </AxButton>

                            <AxButton color="neutral" type="link" icononly>
                              @
                            </AxButton>
                          </div>
                        }
                      ></AxThread>
                    </div>
                  }
                >
                  <p className="title regular l">Right click here.</p>
                </AxDropdown>
              </div>
            </Space>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Comment;
